import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DescriptionIcon from "@mui/icons-material/Description";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { motion } from "framer-motion";
import { RootState } from "../../stores";
import { Plan } from "../../features/plan-management-module/planAction";
import { ArrowBack } from "@mui/icons-material";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5),
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "28px",
  fontWeight: 700,
  color: "#455354",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
  borderRadius: "12px",
  padding: theme.spacing(2.5),
  border: "1px solid #e0e0e0",
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease-in-out",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
}));

const FeatureList = styled(List)(({ theme }) => ({
  padding: 0,
  "& .MuiListItem-root": {
    padding: theme.spacing(0.75, 0),
    "&:hover": {
      backgroundColor: "rgba(75, 91, 93, 0.04)",
      borderRadius: "6px",
      transition: "background-color 0.2s ease-in-out",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "32px",
      color: "#4b5b5d",
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.9rem",
      color: "#4b5b5d",
      fontWeight: 500,
      fontFamily: "'Almarai', sans-serif",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.8rem",
      },
    },
  },
}));

const EstimatorList = styled(List)(({ theme }) => ({
  padding: 0,
  "& .MuiListItem-root": {
    padding: theme.spacing(1, 0),
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
      borderRadius: "6px",
      transition: "background-color 0.2s ease-in-out",
    },
    "& .MuiListItemIcon-root": {
      minWidth: "36px",
      color: "#1976d2",
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.9rem",
      color: "#4b5b5d",
      fontWeight: 600,
      fontFamily: "'Almarai', sans-serif",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.8rem",
      },
    },
    "& .MuiListItemText-secondary": {
      fontSize: "0.85rem",
      color: "#666",
      fontFamily: "'Almarai', sans-serif",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
      },
    },
  },
}));

const PriceDisplay = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "center",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const EstimatorChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#e3f2fd",
  color: "#1976d2",
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 28,
  marginRight: theme.spacing(0.75),
  marginBottom: theme.spacing(0.75),
  fontFamily: "'Almarai', sans-serif",
  "& .MuiChip-label": {
    padding: theme.spacing(0, 1.25),
  },
  "& .MuiChip-icon": {
    marginLeft: theme.spacing(0.75),
    color: "#1976d2",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#4b5b5d",
  fontSize: "1.25rem",
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1),
    fontSize: "1.3rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
  },
}));

const ViewPlan: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const { plans } = useSelector((state: RootState) => state.planManagement);
  const selectedPlan = plans.find((plan: Plan) => plan.id === planId) || null;

  console.log("Selected Plan:", selectedPlan);
  console.log("All Plans:", plans);

  useEffect(() => {
    if (!selectedPlan && planId) {
      navigate("/main/subscriptions");
    }
  }, [selectedPlan, planId, navigate]);

  const formatDuration = (duration: string) => {
    switch (duration) {
      case "monthly":
        return "Monthly";
      case "yearly":
        return "Yearly";
      case "custom":
        return "Custom";
      default:
        return duration;
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  // Helper function to render estimator chips
  const renderEstimatorChips = () => {
    const chips = [];

    if (selectedPlan?.isFleetEstimator) {
      chips.push(
        <EstimatorChip
          key="fleet"
          label="Fleet Estimator"
          size="small"
          icon={<DirectionsCarIcon />}
        />
      );
    }

    if (selectedPlan?.isSiteEstimator) {
      chips.push(
        <EstimatorChip
          key="site"
          label="Site Estimator"
          size="small"
          icon={<LocationOnIcon />}
        />
      );
    }

    return chips;
  };

  // Helper function to render estimator limits
  const renderEstimatorLimits = () => {
    if (!selectedPlan) return null;

    const limits = [];

    if (selectedPlan.isFleetEstimator) {
      limits.push(
        <motion.div key="fleet" variants={itemVariants}>
          <ListItem>
            <ListItemIcon>
              <DirectionsCarIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText
              primary="Fleet Estimator"
              secondary={
                selectedPlan.fleetEstimatorReportLimit > 0
                  ? `${selectedPlan.fleetEstimatorReportLimit} Fleet Estimator Reports`
                  : "Fleet Estimator Access"
              }
            />
          </ListItem>
          {selectedPlan.isSiteEstimator && (
            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.08)" }} />
          )}
        </motion.div>
      );
    }

    if (selectedPlan.isSiteEstimator) {
      limits.push(
        <motion.div key="site" variants={itemVariants}>
          <ListItem>
            <ListItemIcon>
              <LocationOnIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText
              primary="Site Estimator"
              secondary={
                selectedPlan.siteEstimatorReportLimit > 0
                  ? `${selectedPlan.siteEstimatorReportLimit} Site Estimator Reports`
                  : "Site Estimator Access"
              }
            />
          </ListItem>
        </motion.div>
      );
    }

    return limits;
  };

  // Extract system features from custom features
  const getSystemFeatures = () => {
    if (!selectedPlan) return [];

    return selectedPlan.features.filter(
      (feature) =>
        feature === "Embed Reports" ||
        feature === "White Label" ||
        feature === "Popular Plan" ||
        feature.includes("Fleet Estimator") ||
        feature.includes("Site Estimator")
    );
  };

  const getCustomFeatures = () => {
    if (!selectedPlan) return [];

    return selectedPlan.features.filter(
      (feature) => !getSystemFeatures().includes(feature)
    );
  };

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Content>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <WelcomeText>Plan Details</WelcomeText>
              <Tooltip title="Back">
                <StyledBackButton
                  aria-label="back"
                  onClick={() => navigate("/main/subscriptions")}
                >
                  <ArrowBack />
                </StyledBackButton>
              </Tooltip>
            </Box>

            {selectedPlan ? (
              <motion.div
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Plan Header Card */}
                <InfoCard sx={{ textAlign: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      margin: "0 auto 16px",
                      background:
                        "linear-gradient(to bottom, #758d8e, #4b5b5d)",
                      fontSize: "2rem",
                      fontWeight: 600,
                      fontFamily: "'Almarai', sans-serif",
                    }}
                  >
                    {selectedPlan.planName.charAt(0).toUpperCase()}
                  </Avatar>

                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight={700}
                    color="#4b5b5d"
                    gutterBottom
                    fontFamily="'Almarai', sans-serif"
                  >
                    {selectedPlan.planName}
                  </Typography>

                  {/* Estimator Chips */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      mb: 2,
                    }}
                  >
                    {renderEstimatorChips()}
                  </Box>

                  <PriceDisplay>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight={700}
                      color="#4b5b5d"
                      fontFamily="'Almarai', sans-serif"
                    >
                      ${selectedPlan.price}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="#4b5b5d"
                      fontWeight={600}
                      fontFamily="'Almarai', sans-serif"
                    >
                      / {formatDuration(selectedPlan.duration)}
                    </Typography>
                  </PriceDisplay>

                  {selectedPlan.isPopular && (
                    <Chip
                      label="Popular Plan"
                      size="medium"
                      color="success"
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        fontFamily: "'Almarai', sans-serif",
                      }}
                    />
                  )}
                </InfoCard>

                <Grid container spacing={2}>
                  {/* Plan Information */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <InfoCard>
                      <SectionTitle>
                        <AttachMoneyIcon />
                        Plan Information
                      </SectionTitle>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FieldLabel>Price</FieldLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <AttachMoneyIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              color="#4b5b5d"
                              fontFamily="'Almarai', sans-serif"
                            >
                              ${selectedPlan.price}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FieldLabel>Duration</FieldLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ScheduleIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              color="#4b5b5d"
                              fontFamily="'Almarai', sans-serif"
                            >
                              {formatDuration(selectedPlan.duration)}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <FieldLabel>Plan Type</FieldLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <AnalyticsIcon sx={{ mr: 1, color: "#4b5b5d" }} />
                            <Chip
                              label={selectedPlan.mode}
                              size="medium"
                              sx={{
                                backgroundColor:
                                  selectedPlan.mode === "public"
                                    ? "#E7F5FF"
                                    : "#F3F0FF",
                                color:
                                  selectedPlan.mode === "public"
                                    ? "#0B7285"
                                    : "#5F3DC4",
                                fontWeight: 600,
                                textTransform: "capitalize",
                                fontFamily: "'Almarai', sans-serif",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </InfoCard>
                  </Grid>

                  {/* Description */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <InfoCard>
                      <SectionTitle>
                        <DescriptionIcon />
                        Description
                      </SectionTitle>
                      <CardContent
                        sx={{
                          backgroundColor: "#f5f0e7",
                          borderRadius: "8px",
                          p: 2,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="#4b5b5d"
                          sx={{
                            lineHeight: 1.6,
                            fontFamily: "'Almarai', sans-serif",
                          }}
                        >
                          {selectedPlan.description}
                        </Typography>
                      </CardContent>
                    </InfoCard>
                  </Grid>
                </Grid>

                {/* Estimator Limits Section */}
                {(selectedPlan.isFleetEstimator ||
                  selectedPlan.isSiteEstimator) && (
                  <InfoCard>
                    <SectionTitle>
                      <AnalyticsIcon />
                      Estimator Features
                    </SectionTitle>
                    <EstimatorList>{renderEstimatorLimits()}</EstimatorList>
                  </InfoCard>
                )}

                {/* Additional Features */}
                {(selectedPlan.isEmbedded || selectedPlan.isWhiteList) && (
                  <InfoCard>
                    <SectionTitle>
                      <CheckIcon />
                      Additional Features
                    </SectionTitle>
                    <FeatureList>
                      {selectedPlan.isEmbedded && (
                        <motion.div variants={itemVariants}>
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="medium" />
                            </ListItemIcon>
                            <ListItemText primary="Embed Reports" />
                          </ListItem>
                        </motion.div>
                      )}
                      {selectedPlan.isWhiteList && (
                        <motion.div variants={itemVariants}>
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="medium" />
                            </ListItemIcon>
                            <ListItemText primary="White Label" />
                          </ListItem>
                        </motion.div>
                      )}
                    </FeatureList>
                  </InfoCard>
                )}

                {/* Custom Features */}
                {getCustomFeatures().length > 0 && (
                  <InfoCard>
                    <SectionTitle>
                      <CheckIcon />
                      Plan Features
                    </SectionTitle>
                    <FeatureList>
                      {getCustomFeatures().map((feature, index) => (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <ListItem>
                            <ListItemIcon>
                              <CheckIcon fontSize="medium" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                          {index < getCustomFeatures().length - 1 && (
                            <Divider
                              sx={{ borderColor: "rgba(0, 0, 0, 0.08)" }}
                            />
                          )}
                        </motion.div>
                      ))}
                    </FeatureList>
                  </InfoCard>
                )}
              </motion.div>
            ) : (
              <InfoCard sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  color="#4b5b5d"
                  fontFamily="'Almarai', sans-serif"
                >
                  Plan not found
                </Typography>
              </InfoCard>
            )}
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default ViewPlan;
